import { Badge } from '@/design-system/badge/badge';
import { Card } from '@/design-system/card/card';
import { Inline } from '@/design-system/inline/inline';
import { Stack } from '@/design-system/stack/stack';
import { Tooltip } from '@/design-system/tooltip/tooltip';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { getAmountWithCurrency } from '@/utils/get-amount-with-currency';
import { tw } from '@/utils/tw';
import { Currency } from '@seapoint/server/enums';
import { ArrowDownIcon, ArrowLeftRightIcon, ArrowUpRightIcon } from 'lucide-react';

type AccountBalanceCardProps = {
  currency: Currency;
  accountBalance: number;
  pendingDepositsBalance: number;
  pendingWithdrawalsBalance: number;
};

export const AccountBalanceCard = ({
  currency,
  accountBalance,
  pendingDepositsBalance,
  pendingWithdrawalsBalance,
}: AccountBalanceCardProps) => {
  const hasPendingDeposit = pendingDepositsBalance !== 0;
  const hasPendingWithdrawal = pendingWithdrawalsBalance !== 0;
  const hasAnyPending = hasPendingDeposit || hasPendingWithdrawal;

  const badgeVariantConfig = getBadgeVariantConfig({
    pendingDepositsBalance,
    pendingWithdrawalsBalance,
    currency,
  });
  const badgeContent = getPendingBadgeContent({
    variantConfig: badgeVariantConfig,
  });

  return (
    <Card className="flex-1 sm:min-w-[340px]">
      <Stack space={3} className="p-6">
        <Inline space={1} align="center">
          <span className="text-sm font-normal text-muted-foreground">Balance</span>

          {hasAnyPending && (
            <Tooltip
              trigger={
                <Badge variant="neutral" className="gap-1">
                  <Inline space={1} align="center" wrap="nowrap">
                    {badgeContent.icon}

                    <p className="text-xs font-normal text-muted-foreground">{badgeContent.label}</p>
                  </Inline>
                </Badge>
              }
              content={badgeContent.tooltipContent}
              contentAlign="center"
              contentClassName="bg-foreground text-background"
              arrowConfig={{ isVisible: true, className: tw`bg-foreground fill-foreground` }}
            />
          )}
        </Inline>

        <p className="truncate text-2xl font-medium text-foreground">
          {getAmountWithCurrency({ amount: accountBalance, currency, isAccountBalance: true })}
        </p>
      </Stack>
    </Card>
  );
};

type PendingBadgeVariantConfig =
  | { variant: 'NO_PENDING' }
  | { variant: 'BOTH'; pendingDepositsBalance: number; pendingWithdrawalsBalance: number; currency: Currency }
  | { variant: 'DEPOSIT'; pendingDepositsBalance: number; currency: Currency }
  | { variant: 'WITHDRAWAL'; pendingWithdrawalsBalance: number; currency: Currency };

type GetPendingBadgeVariantConfigArgs = {
  pendingDepositsBalance: number;
  pendingWithdrawalsBalance: number;
  currency: Currency;
};

const getBadgeVariantConfig = ({
  pendingDepositsBalance,
  pendingWithdrawalsBalance,
  currency,
}: GetPendingBadgeVariantConfigArgs): PendingBadgeVariantConfig => {
  if (!pendingDepositsBalance && !pendingWithdrawalsBalance) {
    return { variant: 'NO_PENDING' };
  }

  if (pendingDepositsBalance && pendingWithdrawalsBalance) {
    return { variant: 'BOTH', pendingDepositsBalance, pendingWithdrawalsBalance, currency };
  }

  if (pendingDepositsBalance) {
    return { variant: 'DEPOSIT', pendingDepositsBalance, currency };
  }

  return { variant: 'WITHDRAWAL', pendingWithdrawalsBalance, currency };
};

type GetPendingBadgeContentArgs = {
  variantConfig: PendingBadgeVariantConfig;
};

const getPendingBadgeContent = ({ variantConfig }: GetPendingBadgeContentArgs) => {
  const { variant } = variantConfig;

  const iconClassNames = tw`h-3 w-3 text-muted-foreground`;
  const tooltipTextClassNames = tw`text-xs font-normal`;

  switch (variant) {
    case 'NO_PENDING':
      return {
        icon: null,
        label: null,
      };
    case 'BOTH':
      return {
        icon: <ArrowLeftRightIcon className={iconClassNames} aria-hidden="true" />,
        label: 'Pending transactions',
        tooltipContent: (
          <Stack>
            <Inline justify="between" wrap="nowrap" className="w-full">
              <p className={tooltipTextClassNames}>Deposit:</p>

              <p className={tooltipTextClassNames}>
                +
                {getAmountWithCurrency({
                  amount: variantConfig.pendingDepositsBalance,
                  currency: variantConfig.currency,
                  isAccountBalance: true,
                })}
              </p>
            </Inline>

            <Inline justify="between" className="w-full">
              <p className={tooltipTextClassNames}>Withdrawal:</p>

              <p className={tooltipTextClassNames}>
                -
                {getAmountWithCurrency({
                  amount: variantConfig.pendingWithdrawalsBalance,
                  currency: variantConfig.currency,
                  isAccountBalance: true,
                })}
              </p>
            </Inline>
          </Stack>
        ),
      };
    case 'DEPOSIT':
      return {
        icon: <ArrowDownIcon className={iconClassNames} aria-hidden="true" />,
        label: 'Pending deposit',
        tooltipContent: (
          <p className={tooltipTextClassNames}>
            +
            {getAmountWithCurrency({
              amount: variantConfig.pendingDepositsBalance,
              currency: variantConfig.currency,
              isAccountBalance: true,
            })}
          </p>
        ),
      };
    case 'WITHDRAWAL':
      return {
        icon: <ArrowUpRightIcon className={iconClassNames} aria-hidden="true" />,
        label: 'Pending withdrawal',
        tooltipContent: (
          <p className={tooltipTextClassNames}>
            {getAmountWithCurrency({
              amount: variantConfig.pendingWithdrawalsBalance,
              currency: variantConfig.currency,
              isAccountBalance: true,
            })}
          </p>
        ),
      };
    default:
      return exhaustiveCheck(variant);
  }
};
